import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Divider,
  useTheme,
} from "@mui/material";

export default function Evaluation({    
  evaluationResults, 
  evaluationSummary, 
  evaluationType,  
   }) {
  const theme = useTheme();

  if (!evaluationResults || evaluationResults.length === 0) {
    return (
      <Typography variant="body1" sx={{ mt: 2, color: theme.palette.text.secondary }}>
        No evaluation results available.
      </Typography>
    );
  }

  const summary = evaluationResults.summary || null;
  const resultsArray = Array.isArray(evaluationResults)
    ? evaluationResults
    : evaluationResults.results || [];

  return (
    <Box
      sx={{
        mt: 5,
        mb: 5,
        px: 2,
        maxWidth: "800px",
        mx: "auto",
      }}
    >
      <Typography
        variant="h5"
        gutterBottom
        sx={{
          fontWeight: 600,
          textAlign: "center",
          color: theme.palette.primary.main,
          mb: 3,
        }}
      >
        {evaluationType} Evaluation Results
      </Typography>

      {/* ✅ Summary Section */}
      {summary && (
        <Card
          elevation={3}
          sx={{
            mb: 4,
            borderRadius: 3,
            p: 2.5,
            backgroundColor: theme.palette.grey[50],
          }}
        >
          <Typography
            variant="subtitle1"
            gutterBottom
            sx={{ fontWeight: 600, color: theme.palette.text.primary }}
          >
            📊 {evaluationType} Evaluation Summary
          </Typography>
          <Divider sx={{ mb: 1.5 }} />
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>Total Questions:</strong> {summary.total_questions}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>Average Score:</strong> {summary.average_score}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>MCQ Accuracy:</strong>{" "}
            {summary.mcq_accuracy !== null
              ? `${Math.round(summary.mcq_accuracy * 100)}%`
              : "N/A"}
          </Typography>
          <Typography variant="body2">
            <strong>Short Answer Similarity:</strong>{" "}
            {summary.avg_shortanswer_similarity !== null
              ? `${Math.round(summary.avg_shortanswer_similarity * 100)}%`
              : "N/A"}
          </Typography>
        </Card>
      )}

      {/* ✅ Per-question Results */}
      {resultsArray.map((item, idx) => (
        <Card
          key={idx}
          elevation={2}
          sx={{
            mb: 3,
            p: 2,
            borderRadius: 3,
            transition: "0.2s ease",
            "&:hover": {
              boxShadow: theme.shadows[6],
              transform: "scale(1.01)",
            },
          }}
        >
          <CardContent>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: theme.palette.primary.dark,
                mb: 1,
              }}
            >
              Q{idx + 1}. {item.question}
            </Typography>

            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Your Answer:</strong> {item.answer || "Not answered"}
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Expected Answer:</strong> {item.expected_answer}
            </Typography>

            <Divider sx={{ my: 1.5 }} />

            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Score:</strong>{" "}
              <span
                style={{
                  color:
                    item.score >= 0.9
                      ? theme.palette.success.main
                      : theme.palette.error.main,
                  fontWeight: 600,
                }}
              >
                {item.score?.toFixed(2) ?? "0.00"}
              </span>
            </Typography>

            <Typography
              variant="body2"
              sx={{
                mb: 0.5,
                color:
                  item.grade === "Correct"
                    ? theme.palette.success.main
                    : theme.palette.error.main,
                fontWeight: 600,
              }}
            >
              <strong>Grade:</strong>{" "}
              {item.grade || (item.score >= 0.9 ? "Correct" : "Incorrect")}
            </Typography>

            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.secondary }}
            >
              <strong>Feedback:</strong>{" "}
              {item.feedback || "No feedback provided."}
            </Typography>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
}
